// Market gift synchronization, shared by the drawer and one-click checkout.
// Wanted gifts come from df-cart-tiers (custom.offres_panier_v2, WEBLCFB2C-227); paid items are never retried here.
(() => {
  let pending = null;
  let requested = false;
  let checkoutRequested = false;
  let unknownGiftAdd = false;
  let needsRefresh = false;
  const drawer = () => document.querySelector('cart-drawer');
  const sectionId = () => document.querySelector('[id^="shopify-section-"][id$="__cart-drawer"]')?.id.replace('shopify-section-', '');

  async function json(path, body) {
    const response = await fetch(`${Shopify.routes.root}${path}`, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: AbortSignal.timeout(15000)
    } : { signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw Object.assign(new Error(`Gift synchronization failed (${response.status})`), { rejected: response.status >= 400 && response.status < 500, status: response.status });
    return response.json();
  }

  function giftError(error) {
    return Object.assign(new Error((error?.unavailable ? drawer()?.dataset.dfGiftUnavailableText : drawer()?.dataset.dfGiftErrorText) || drawer()?.dataset.dfRequestErrorText || 'Please check your cart.'), { gift: true, review: Boolean(error?.review), unavailable: Boolean(error?.unavailable) });
  }
  function reportError(error) {
    const host = window.themeVariables?.settings.pageType === 'cart' ? document.querySelector('main') : drawer();
    window.dfCartActions?.report(giftError(error), host, () => autoSync(), 'gift');
  }

  // Cart and wanted gifts come from the df-cart-state section (df-cart-tiers): same tiers, conditions
  // and stock rule as the drawer and the discount, read fresh after every change.
  async function readState() {
    const response = await fetch(`${Shopify.routes.root}?sections=df-cart-state`, { cache: 'no-store', signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw Object.assign(new Error(`Gift state failed (${response.status})`), { status: response.status });
    const html = (await response.json())['df-cart-state'];
    const state = JSON.parse(new DOMParser().parseFromString(html, 'text/html').querySelector('[data-df-cart-state]').textContent);
    return { cart: state.cart, wanted: (state.offer?.wanted || []).map(Number), adapter: Number(state.adapterVariant) || 0 };
  }

  async function reconcile() {
    if (!drawer()?.querySelector('[data-df-gift]')) return;
    const sections = sectionId();
    let fresh = null;
    let changed = false;
    // Bounded: one request per pass, never an add loop when another app keeps changing the cart.
    for (let attempt = 0; attempt < 8; attempt++) {
      const state = await readState();
      fresh = state.cart;
      const count = id => state.wanted.filter(wanted => wanted === id).length;
      const gifts = fresh.items.filter(item => item.properties?._gift && item.variant_id !== state.adapter);
      const missing = state.wanted.find(id => !gifts.some(item => item.variant_id === id));
      // A lost add response is only recovered when the gift is actually found. A later
      // refresh/event must not authorize another additive request while its result is unknown.
      if (unknownGiftAdd && missing) throw Object.assign(new Error('Gift result is still unknown'), { review: true });
      if (!missing) unknownGiftAdd = false;
      const seen = new Set();
      const wrong = gifts.find(item => {
        const duplicate = seen.has(item.variant_id);
        seen.add(item.variant_id);
        return duplicate ? true : item.quantity !== count(item.variant_id);
      });
      if (wrong) {
        needsRefresh = true;
        const duplicate = gifts.indexOf(wrong) !== gifts.findIndex(item => item.variant_id === wrong.variant_id);
        await json('cart/change.js', { id: wrong.key, quantity: duplicate ? 0 : count(wrong.variant_id) });
        changed = true;
        continue;
      }
      if (missing) {
        needsRefresh = true;
        try {
          await json('cart/add.js', { items: [{ id: missing, quantity: count(missing), properties: { _gift: '1' } }] });
          changed = true;
          continue;
        } catch (error) {
          if (!error.rejected) { unknownGiftAdd = true; error.review = true; }
          if (error.status === 422) {
            // Confirm the additive request did not partially succeed before offering a bypass.
            const after = (await readState()).cart.items.filter(item => item.properties?._gift && item.variant_id === missing);
            if (after.length) { changed = true; continue; }
            error.unavailable = true;
          }
          throw error;
        }
      }
      break;
    }
    if (changed || needsRefresh) {
      const response = await fetch(`${Shopify.routes.root}?section_id=${encodeURIComponent(sections)}`);
      if (!response.ok) throw new Error('Gift section refresh failed');
      fresh.sections = { [sections]: await response.text() };
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true, detail: { baseEvent: 'df:gift', cart: fresh }
      }));
      needsRefresh = false;
      // Full cart has no section listener. Checkout consumes fresh server totals directly.
      if (!checkoutRequested && window.themeVariables?.settings.pageType === 'cart') window.location.reload();
    }
    document.querySelectorAll('[data-df-action-error="gift"]').forEach(message => message.remove());
  }

  function sync({ checkout = false } = {}) {
    if (checkout) checkoutRequested = true;
    requested = true;
    if (!pending) {
      pending = (async () => {
        do {
          requested = false;
          await reconcile();
        } while (requested);
      })().catch(error => { throw giftError(error); }).finally(() => { pending = null; checkoutRequested = false; });
    }
    return pending;
  }

  const autoSync = () => sync().catch(reportError);
  function syncRenderedState() {
    const node = drawer()?.querySelector('[data-df-gift]');
    if (!node) return;
    const offer = JSON.parse(node.textContent);
    // A fresh, already-consistent section needs no extra cart request on every page load.
    if ((offer.wanted || []).slice().sort().join() !== (offer.gifts || []).slice().sort().join()) autoSync();
  }
  document.addEventListener('cart:change', event => {
    if (event.detail?.baseEvent !== 'df:gift') autoSync();
  });
  document.addEventListener('cart-drawer:refreshed', () => {
    if (!pending) syncRenderedState();
  });
  window.dfCartGift = { sync };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncRenderedState, { once: true });
  else syncRenderedState();
})();
